import { logNotification, LogLevel, Category } from './OTelNotifications';

enum TokenType {
  Aria = 0,
  Nexus = 1
}

export namespace TenantTokenManager {
  let ariaTokenMap: { [eventName: string]: string } = {};
  let nexusTokenMap: { [eventName: string]: number } = {};

  // tslint:disable-next-line:no-any
  let tenantTokens: any = {};

  export function setTenantToken(namespace: string, ariaTenantToken?: string, nexusTenantToken?: number) {
    let parts = namespace.split('.');

    // Ensure that the provided namespace is valid
    if (parts.length < 2 || parts[0] !== 'Office') {
      logNotification(LogLevel.Error, Category.Core, () => {
        return `Invalid namespace: ${namespace}`;
      });
      return;
    }

    // Create the leaf node first
    let leaf: any = Object.create(Object.prototype);
    if (ariaTenantToken) {
      leaf['ariaTenantToken'] = ariaTenantToken;
    }
    if (nexusTenantToken) {
      leaf['nexusTenantToken'] = nexusTenantToken;
    }

    // Then walk backwards adding the path to the root
    let node = leaf;
    let index;
    for (index = parts.length - 1; index >= 0; --index) {
      let parentNode: any = Object.create(Object.prototype);
      parentNode[parts[index]] = node;
      node = parentNode;
    }

    setTenantTokens(node);
  }

  export function setTenantTokens(tokenTree: object) {
    if (typeof tokenTree !== 'object') {
      throw new Error('tokenTree must be an object');
    }
    tenantTokens = mergeTenantTokens(tenantTokens, tokenTree);
  }

  export function getTenantTokens(eventName: string) {
    let ariaTenantToken = getAriaTenantToken(eventName);
    let nexusTenantToken = getNexusTenantToken(eventName);
    if (!nexusTenantToken || !ariaTenantToken) {
      throw new Error('Could not find tenant token');
    }
    return {
      ariaTenantToken: ariaTenantToken,
      nexusTenantToken: nexusTenantToken
    };
  }

  export function getAriaTenantToken(eventName: string): string | undefined {
    // Return cached token if it exists for eventName
    if (ariaTokenMap[eventName]) {
      return ariaTokenMap[eventName];
    }

    const ariaToken = getTenantToken(eventName, TokenType.Aria);
    if (typeof ariaToken === 'string') {
      ariaTokenMap[eventName] = ariaToken;
      return <string>ariaToken;
    }
    return undefined;
  }

  export function getNexusTenantToken(eventName: string): number | undefined {
    // Return cached token if it exists for eventName
    if (nexusTokenMap[eventName]) {
      return nexusTokenMap[eventName];
    }

    const nexusToken = getTenantToken(eventName, TokenType.Nexus);
    if (typeof nexusToken === 'number') {
      nexusTokenMap[eventName] = nexusToken;
      return <number>nexusToken;
    }
    return undefined;
  }

  function getTenantToken(eventName: string, tokenType: TokenType): string | number | undefined {
    // Find token
    let pieces = eventName.split('.');
    let node = tenantTokens;
    let token: string | number | undefined = undefined;

    if (!node) {
      return undefined;
    }

    for (let i = 0; i < pieces.length - 1; i++) {
      if (node[pieces[i]]) {
        node = node[pieces[i]];

        if (tokenType === TokenType.Aria && typeof node.ariaTenantToken === 'string') {
          // set token if one is available
          token = node.ariaTenantToken;
        } else if (tokenType === TokenType.Nexus && typeof node.nexusTenantToken === 'number') {
          token = node.nexusTenantToken;
        }
      }
    }

    return token;
  }

  // tslint:disable-next-line:no-any
  function mergeTenantTokens(existingTokenTree: any, newTokenTree: any) {
    if (typeof newTokenTree !== 'object') {
      return newTokenTree;
    }
    for (let key of Object.keys(newTokenTree)) {
      if (key in existingTokenTree && typeof (existingTokenTree[key] === 'object')) {
        existingTokenTree[key] = mergeTenantTokens(existingTokenTree[key], newTokenTree[key]);
      } else {
        existingTokenTree[key] = newTokenTree[key];
      }
    }
    return existingTokenTree;
  }

  export function clear() {
    ariaTokenMap = {};
    nexusTokenMap = {};
    tenantTokens = {};
  }
}
